<template>
  <section class="form-section">
    <div class="row align-justify">
      <div class="small-12 columns">
        <ElForm
          ref="accountInformation"
          class="card"
          autocomplete="off"
          :model="accountInformationModel"
          :rules="rules"
          label-position="top"
          @submit.native.prevent="submitAccountInformationForm"
        >
          <div class="card__block">
            <ElFormItem :label="$t('EMAIL')" prop="email">
              <ElInput
                :value="email"
                dir="ltr"
                :placeholder="$t('EMAIL')"
                type="email"
                name="email"
                class="text-latin"
                disabled
              />
              <FieldError :errors="errors" field="email" />
            </ElFormItem>

            <ElFormItem
              :label="$t('PUBLISHER_ACCOUNT.COMPANY_FULL_NAME')"
              prop="name"
            >
              <ElInput
                v-model="accountInformationModel.name"
                dir="ltr"
                name="name"
              />
              <FieldError :errors="errors" field="name" />
              <p class="el-form-item__help">
                <i class="el-icon-warning" />
                {{ $t('AUTH.STEPS.NAME_WARNING') }}
              </p>
            </ElFormItem>

            <ElFormItem :label="$t('MOBILE_PHONE')" prop="phone">
              <ElInput
                v-model="accountInformationModel.phone"
                type="tel"
                dir="ltr"
                name="phone"
                placeholder="00989121234567"
                :maxlength="20"
              />

              <FieldError :errors="errors" field="phone" />
              <p class="el-form-item__help">
                <i class="el-icon-warning" />
                {{ $t('AUTH.STEPS.CELLPHONE_WARNING') }}
              </p>
            </ElFormItem>

            <ElFormItem :label="$t('POSTAL_CODE')" prop="postal_code">
              <ElInput
                v-model="accountInformationModel.postal_code"
                dir="ltr"
                name="postal_code"
                :maxlength="30"
              />
              <FieldError :errors="errors" field="postal_code" />
            </ElFormItem>

            <ElFormItem :label="$t('CITY')" prop="city" required>
              <ElInput v-model="accountInformationModel.city" name="city" />
              <FieldError :errors="errors" field="city" />
            </ElFormItem>

            <ElFormItem :label="$t('ADDRESS')" prop="address">
              <ElInput
                v-model="accountInformationModel.address"
                :autosize="{ minRows: 2, maxRows: 4 }"
                name="address"
                dir="ltr"
                :maxlength="150"
                type="textarea"
              />
              <FieldError :errors="errors" field="address" />
            </ElFormItem>

            <ElFormItem
              :label="$t('PUBLISHER_ACCOUNT.REGISTRATION_NUMBER')"
              prop="registration_number"
            >
              <ElInput
                v-model="accountInformationModel.registration_number"
                name="registration_number"
                dir="ltr"
              />
              <FieldError :errors="errors" field="registration_number" />
            </ElFormItem>

            <ElFormItem
              :label="$t('PUBLISHER_ACCOUNT.COMPANY_CERTIFICATE')"
              prop="company_certificate"
            >
              <Droppable
                :has-preview="true"
                :has-progress="true"
                file-types="image/*"
                :upload-url="uploadPaths.companyCertificate"
                :image-url="accountInformationModel.company_certificate.url"
                @uploaded="uploadCertImage"
              >
                <div slot="label">
                  {{ $t('PUBLISHER_ACCOUNT.DRAG_CERTIFICATION_IMAGE') }}
                </div>
              </Droppable>
              <FieldError :errors="errors" field="company_certificate" />
            </ElFormItem>

            <div class="auth-steps__navigation">
              <ElButton type="default" @click.native="handlePrevPage">
                {{ $t('AUTH.STEPS.PREV_LEVEL_BTN') }}
              </ElButton>
              <ElButton type="primary" native-type="submit" :loading="loading">
                {{ $t('AUTH.STEPS.NEXT_LEVEL_BTN') }}
              </ElButton>
            </div>

            <ErrorsList v-if="errors" :errors="errors" />
          </div>
        </ElForm>
      </div>
    </div>
  </section>
</template>

<script>
import normalizeNumbers from '@/utils/normalizeNumbers';

import Droppable from '@@/layout/Droppable';

import form from '@/mixins/form';

import { Publishers } from '@/api/api';
import ErrorsList from '@@/common/ErrorsList';
import FieldError from '@@/common/FieldError';

export default {
  name: 'ForeignerLegal',

  components: {
    FieldError,
    ErrorsList,
    Droppable,
  },

  mixins: [form],

  props: {
    email: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      accountInformationModel: {
        name: null,
        registration_number: null,
        company_certificate: {
          id: null,
          url: null,
        },

        address: null,
        city: null,
        phone: null,
        postal_code: null,
      },

      uploadPaths: Publishers.uploadPaths,
    };
  },

  computed: {
    rules() {
      const rules = {
        required: true,
        message: this.$t('VALIDATION_REQUIRED_ERR'),
        trigger: 'blur',
      };

      return {
        name: {
          ...rules,
          type: 'string',
        },
        phone: [
          rules,
          {
            pattern: /^\d{3,20}$/i,
            message: this.$t('PUBLISHER_ACCOUNT.INVALID_FOREIGN_PHONE'),
            trigger: 'blur',
          },
        ],
        postal_code: [
          rules,
          {
            min: 3,
            max: 30,
            message: this.$t('PUBLISHER_ACCOUNT.INVALID_POSTAL_CODE'),
            trigger: 'blur',
          },
        ],
        address: {
          ...rules,
          type: 'string',
        },
        city: {
          ...rules,
          type: 'string',
        },
        registration_number: rules,
        company_certificate: rules,
      };
    },
  },

  watch: {
    accountInformationModel: {
      deep: true,
      handler: normalizeNumbers,
    },
  },

  methods: {
    submitAccountInformationForm() {
      if (!this.isValid('accountInformation')) {
        return false;
      }
      this.$emit('submitAccountInformation', this.accountInformationModel);
    },

    handlePrevPage() {
      return this.$emit('prevStep');
    },

    uploadCertImage(src) {
      this.accountInformationModel.company_certificate.id = src;
    },
  },
};
</script>
